import { CheckCircle2, Clock, FileText, Target, Users, Video, ArrowRight, Calendar, Briefcase, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const ThankYouBooking = () => {
  const whatToPrepare = [
    {
      icon: Target,
      title: "Your Revenue Goals",
      description: "Current revenue, target revenue, and the timeline you're working towards"
    },
    {
      icon: Users,
      title: "Ideal Customer Profile",
      description: "Who your best customers are and what makes them valuable to your business"
    },
    {
      icon: Briefcase,
      title: "Current Marketing Efforts",
      description: "What channels you've tried, what's working, and what's not"
    },
    {
      icon: FileText,
      title: "Any Existing Collateral",
      description: "Case studies, testimonials, or results you can share (optional but helpful)"
    }
  ];

  const whatWeWillCover = [
    {
      icon: Lightbulb,
      title: "Growth Opportunity Analysis",
      description: "We'll identify the biggest revenue opportunities you're currently missing"
    },
    {
      icon: Target,
      title: "Custom Acquisition Strategy",
      description: "A tailored plan for generating qualified leads in your specific industry"
    },
    {
      icon: Clock,
      title: "90-Day Roadmap",
      description: "Clear next steps to start generating predictable pipeline growth"
    }
  ];

  return (
    <>
      <EnhancedSEO
        title="You're All Booked | Vincent Growth Partners"
        description="Your strategic consultation is confirmed. Here's what to prepare for our call."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-primary via-primary to-primary-dark">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary/20 mb-8 animate-pulse">
              <CheckCircle2 className="w-10 h-10 text-secondary" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              You're All Booked!
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
              Your strategic consultation has been confirmed. Check your email for the calendar invite.
            </p>
            
            <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/20 rounded-full px-6 py-3 text-secondary">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">Calendar invite sent to your email</span>
            </div>
          </div>
        </section>

        {/* VSL Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Video className="w-8 h-8 text-secondary" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Watch Before Your Call
                </h2>
              </div>
              
              <p className="text-white/70 mb-8 text-lg">
                This quick video will help you get the most value from our conversation:
              </p>
              
              {/* VSL Video Container - Replace VIDEO_ID_HERE with your actual video ID */}
              {/* Options: 
                  - YouTube: <iframe src="https://www.youtube.com/embed/VIDEO_ID" ... />
                  - Vimeo: <iframe src="https://player.vimeo.com/video/VIDEO_ID" ... />
                  - Loom: <iframe src="https://www.loom.com/embed/VIDEO_ID" ... />
                  - Wistia: <iframe src="//fast.wistia.net/embed/iframe/VIDEO_ID" ... />
              */}
              <div className="aspect-video bg-black/40 rounded-xl border-2 border-dashed border-secondary/30 flex items-center justify-center mb-6 relative overflow-hidden">
                {/* PLACEHOLDER - Replace this entire div with your video iframe when ready */}
                <div className="text-center p-8">
                  <div className="w-24 h-24 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6 animate-pulse">
                    <div className="w-0 h-0 border-t-[16px] border-t-transparent border-l-[28px] border-l-secondary border-b-[16px] border-b-transparent ml-2" />
                  </div>
                  <h3 className="text-white text-xl font-semibold mb-2">Your Video Goes Here</h3>
                  <p className="text-white/60 text-sm max-w-md mx-auto mb-4">
                    Film a 2-3 minute video covering what to expect on the call and what to prepare
                  </p>
                  <div className="bg-white/10 rounded-lg p-4 text-left max-w-sm mx-auto">
                    <p className="text-white/80 text-xs font-medium mb-2">Suggested topics:</p>
                    <ul className="text-white/60 text-xs space-y-1">
                      <li>• What the strategy call will cover</li>
                      <li>• What information to have ready</li>
                      <li>• What questions you'll be asking</li>
                      <li>• What they can expect as next steps</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <p className="text-white/50 text-sm text-center">
                Watching this video will ensure we make the most of our time together
              </p>
            </div>
          </div>
        </section>

        {/* What to Prepare Section */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                What to Prepare for Your Call
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">
                Having this information ready will help us provide maximum value in our conversation
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {whatToPrepare.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-white/60">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Will Cover Section */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                What We'll Cover on the Call
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">
                This isn't a generic sales pitch — it's a strategic conversation tailored to your business
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {whatWeWillCover.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-colors"
                >
                  <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 pb-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-gradient-to-br from-secondary/20 to-secondary/5 border border-secondary/20 rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Questions Before Your Call?
              </h2>
              <p className="text-white/70 mb-6">
                If you have any questions or need to reschedule, simply reply to your calendar invite email.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="premium"
                  size="lg"
                  asChild
                >
                  <Link to="/">
                    Return to Homepage
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-white/20 text-white hover:bg-white/10"
                  asChild
                >
                  <Link to="/resources">
                    Browse Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ThankYouBooking;
